import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, CheckIcon, AlertCircleIcon, ArrowRightIcon, UndoIcon, TagIcon, PackageIcon, DollarSignIcon, ClockIcon, ShoppingBagIcon, BoxIcon } from 'lucide-react';
import type { Product } from '../utils/mockData';
interface ComparisonCardProps {
  product: Product;
  onLink: () => void;
  onSkip?: () => void;
  isSelected?: boolean;
  onSelect?: () => void;
  showCheckbox?: boolean;
}
const ComparisonCard = ({
  product,
  onLink,
  onSkip,
  isSelected,
  onSelect,
  showCheckbox = false
}: ComparisonCardProps) => {
  const [isLinked, setIsLinked] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);
  const isPerfectMatch = product.matchCategory === 'perfect-match';
  const amazonProduct = product.amazonProduct;
  if (!amazonProduct) return null;
  const handleLink = () => {
    setIsLinked(true);
    onLink();
  };
  const handleUndo = () => {
    if (isLinked) {
      setIsLinked(false);
    } else if (isSkipped) {
      setIsSkipped(false);
    }
  };
  const handleSkip = () => {
    setIsSkipped(true);
    onSkip?.();
  };
  const ComparisonRow = ({
    label,
    shopify,
    amazon,
    icon
  }) => <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-27">
      <div className="flex items-center space-x-2" data-id="element-28">
        <div className="w-6" data-id="element-29">{icon}</div>
        <span className="text-gray-600" data-id="element-30">{shopify}</span>
      </div>
      <div className="flex items-center space-x-2" data-id="element-31">
        <div className="w-6" data-id="element-32">{icon}</div>
        <span className="text-gray-800" data-id="element-33">{amazon}</span>
      </div>
    </div>;
  return <motion.div layout className={`bg-white rounded-lg shadow-sm overflow-hidden border relative ${isLinked ? 'border-green-200' : isSkipped ? 'border-purple-200' : isSelected ? 'border-blue-200' : 'border-gray-200'}`} data-id="element-34">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10" data-id="element-35">
        <motion.button onClick={onSelect} className={`p-2 rounded-md border ${isSelected ? 'bg-blue-500 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-400 hover:border-blue-500 hover:text-blue-500'}`} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-36">
          <CheckIcon className="h-4 w-4" data-id="element-37" />
        </motion.button>
      </div>
      <div className={`${showCheckbox ? 'pl-14' : ''}`} data-id="element-38">
        {isLinked || isSkipped ? <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className={`p-3 flex items-center justify-between ${isLinked ? 'bg-green-50' : 'bg-purple-50'}`} data-id="element-39">
            <div className="flex items-center min-w-0" data-id="element-40">
              <CheckIcon className={`h-4 w-4 flex-shrink-0 mr-2 ${isLinked ? 'text-green-500' : 'text-purple-500'}`} data-id="element-41" />
              <div className="min-w-0" data-id="element-42">
                <span className={`text-sm font-medium truncate block ${isLinked ? 'text-green-700' : 'text-purple-700'}`} data-id="element-43">
                  {product.title}
                </span>
                <span className={`text-xs ${isLinked ? 'text-green-600' : 'text-purple-600'}`} data-id="element-44">
                  {isLinked ? 'Product Added for Linking' : 'Product Marked for Skip'}
                </span>
              </div>
            </div>
            <motion.button onClick={handleUndo} className={`ml-4 flex items-center text-sm ${isLinked ? 'text-green-600 hover:text-green-700' : 'text-purple-600 hover:text-purple-700'}`} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-45">
              <UndoIcon className="h-4 w-4 mr-1" data-id="element-46" />
              Undo
            </motion.button>
          </motion.div> : <>
            <div className="p-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between" data-id="element-47">
              <div className="flex items-center space-x-2" data-id="element-48">
                {isPerfectMatch ? <CheckIcon className="h-4 w-4 text-green-500" data-id="element-49" /> : <AlertCircleIcon className="h-4 w-4 text-yellow-500" data-id="element-50" />}
                <h3 className="text-sm font-medium text-gray-700" data-id="element-51">
                  {isPerfectMatch ? 'Perfect Match' : 'Potential Match'}
                </h3>
              </div>
              {!isPerfectMatch && amazonProduct.matchConfidence && <span className="text-xs text-gray-500" data-id="element-52">
                  {(amazonProduct.matchConfidence * 100).toFixed(0)}% Confidence
                </span>}
            </div>
            <div className="p-4 space-y-4" data-id="element-53">
              <div className="grid grid-cols-2 gap-8 border-b border-gray-100 pb-4" data-id="element-54">
                <div className="flex items-center space-x-2" data-id="element-55">
                  <ShoppingBagIcon className="h-5 w-5 text-[#7AB55C]" data-id="element-56" />
                  <span className="font-medium text-gray-700" data-id="element-57">
                    Shopify Product
                  </span>
                </div>
                <div className="flex items-center space-x-2" data-id="element-58">
                  <BoxIcon className="h-5 w-5 text-[#FF9900]" data-id="element-59" />
                  <span className="font-medium text-gray-700" data-id="element-60">
                    Amazon Match
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-4" data-id="element-61">
                <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-62">
                  <img src={product.imageUrl} alt={product.title} className="w-full h-full object-cover" data-id="element-63" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-64">
                  <ComparisonRow label="Title" shopify={product.title} amazon={amazonProduct.title} icon={<PackageIcon className="h-4 w-4 text-gray-400" data-id="element-66" />} data-id="element-65" />
                  <ComparisonRow label="ID" shopify={`SKU: ${product.sku}`} amazon={`ASIN: ${amazonProduct.asin}`} icon={<TagIcon className="h-4 w-4 text-gray-400" data-id="element-68" />} data-id="element-67" />
                  <ComparisonRow label="Price" shopify={`$${product.price}`} amazon={`$${amazonProduct.price}`} icon={<DollarSignIcon className="h-4 w-4 text-gray-400" data-id="element-70" />} data-id="element-69" />
                </div>
                <div className="flex-shrink-0 flex flex-col items-end space-y-1" data-id="element-71">
                  <div className="flex items-center text-yellow-500" data-id="element-72">
                    <StarIcon className="h-4 w-4 fill-current" data-id="element-73" />
                    <span className="ml-1 text-sm" data-id="element-74">{amazonProduct.rating}</span>
                  </div>
                  <span className="text-xs text-gray-500" data-id="element-75">
                    {amazonProduct.reviewCount.toLocaleString()} reviews
                  </span>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end space-x-3" data-id="element-76">
              <button onClick={handleSkip} className="text-sm text-gray-600 hover:text-gray-900 flex items-center" data-id="element-77">
                <ClockIcon className="h-4 w-4 mr-1" data-id="element-78" />
                Skip for now
              </button>
              <motion.button onClick={handleLink} className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm flex items-center" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-79">
                Link Products
                <ArrowRightIcon className="h-3 w-3 ml-2" data-id="element-80" />
              </motion.button>
            </div>
          </>}
      </div>
    </motion.div>;
};
export default ComparisonCard;